import { supabase } from './config';

// Map DB rows (snake_case) to the position/trade shape the UI already uses.
export const livePositionFromDb = (r) => ({
  id: r.id,
  symbol: r.symbol,
  address: r.address,
  tokenDecimals: r.token_decimals,
  buyPrice: Number(r.buy_price),
  currentPrice: Number(r.current_price),
  peakPrice: Number(r.peak_price),
  amountSol: Number(r.amount_sol),
  tokens: Number(r.tokens),
  buyPriceUsd: r.buy_price_usd != null ? Number(r.buy_price_usd) : null,
  currentPriceUsd: r.current_price_usd != null ? Number(r.current_price_usd) : null,
  peakPriceUsd: null,
  customHardSL: r.custom_hard_sl,
  customTrailingSL: r.custom_trailing_sl,
  customTrailingAct: r.custom_trailing_act,
  takeProfitHit: r.tp_hit || [],
  milestonesHit: [],
  lossMilestonesHit: [],
  status: r.status,
  isReal: true,
  realTimeStrategy: {
    action: 'LIVE', color: 'text-green-400', bg: 'bg-green-500/10',
    border: 'border-green-500/30', icon: 'FiZap', text: 'Managed by the auto-sell bot.',
  },
});

export const liveTradeFromDb = (r) => ({
  id: r.id,
  symbol: r.symbol,
  address: r.address,
  buyPrice: Number(r.buy_price),
  sellPrice: Number(r.sell_price),
  buyPriceUsd: r.buy_price_usd != null ? Number(r.buy_price_usd) : null,
  sellPriceUsd: r.sell_price_usd != null ? Number(r.sell_price_usd) : null,
  pnlSol: Number(r.pnl_sol),
  pnlPercentage: Number(r.pnl_percentage),
  reason: r.reason,
  txid: r.txid,
  closedAt: r.closed_at,
  isReal: true,
});

export async function fetchLivePositions(userId) {
  const { data } = await supabase
    .from('live_positions').select('*')
    .eq('user_id', userId).eq('status', 'open')
    .order('created_at', { ascending: false });
  return (data || []).map(livePositionFromDb);
}

export async function fetchLiveTrades(userId) {
  const { data } = await supabase
    .from('live_trades').select('*')
    .eq('user_id', userId)
    .order('closed_at', { ascending: false }).limit(100);
  return (data || []).map(liveTradeFromDb);
}

// Persist per-position SL/TP overrides to live_positions (the backend engine reads these).
// Pass null for any field to clear it back to the global bot_config value.
export async function updateLiveStrategy(positionId, { customHardSL, customTrailingSL, customTrailingAct }) {
  const { error } = await supabase
    .from('live_positions')
    .update({
      custom_hard_sl: customHardSL,
      custom_trailing_sl: customTrailingSL,
      custom_trailing_act: customTrailingAct,
    })
    .eq('id', positionId);
  if (error) throw new Error(error.message);
}

// Push settings to bot_config: the normalized columns the backend engine reads,
// plus the full settings object in settings_json so any machine can rehydrate.
export async function syncLiveConfig(userId, settings) {
  const { error } = await supabase.from('bot_config').upsert({
    user_id: userId,
    hard_stop_loss: settings.hardStopLoss,
    trailing_stop_loss: settings.trailingStopLoss,
    trailing_activation_multiplier: settings.trailingActivationMultiplier,
    hard_stop_enabled: settings.hardStopEnabled !== false,
    trailing_stop_enabled: settings.trailingStopEnabled !== false,
    take_profit_enabled: settings.takeProfitEnabled,
    take_profit_tiers: settings.takeProfitTiers,
    settings_json: settings,
  }, { onConflict: 'user_id' });
  if (error) throw new Error(error.message);
}

// Read the user's saved settings (full object) from bot_config. Returns null if none.
export async function fetchUserSettings(userId) {
  const { data } = await supabase
    .from('bot_config').select('settings_json')
    .eq('user_id', userId).maybeSingle();
  return data?.settings_json || null;
}

// --- Watchlist (Supabase-backed, mirrors the localStorage shape used by the UI) ---
export const watchlistItemFromDb = (r) => ({
  id: r.id,
  symbol: r.symbol,
  address: r.address,
  initialPrice: Number(r.initial_price),
  currentPrice: Number(r.initial_price),
  priceNative: Number(r.initial_price),
  initialPriceUsd: r.initial_price_usd != null ? Number(r.initial_price_usd) : null,
  currentPriceUsd: r.initial_price_usd != null ? Number(r.initial_price_usd) : null,
  priceChange24h: 0,
  addedAt: r.added_at,
  milestonesHitUp: [],
  milestonesHitDown: [],
});

export async function fetchWatchlist(userId) {
  const { data } = await supabase
    .from('watchlist').select('*')
    .eq('user_id', userId)
    .order('added_at', { ascending: false });
  return (data || []).map(watchlistItemFromDb);
}

// Insert a watchlist token. Returns the new row mapped to UI shape (or null if it already exists).
export async function addWatchlistItem(userId, { symbol, address, initialPrice, initialPriceUsd }) {
  const { data, error } = await supabase
    .from('watchlist')
    .insert({
      user_id: userId,
      symbol,
      address,
      initial_price: initialPrice,
      initial_price_usd: initialPriceUsd ?? null,
    })
    .select()
    .maybeSingle();
  if (error) {
    if (error.code === '23505') return null; // unique (user_id, address) — already watched
    throw new Error(error.message);
  }
  return data ? watchlistItemFromDb(data) : null;
}

export async function removeWatchlistItem(id) {
  const { error } = await supabase.from('watchlist').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
